import { 
    gql, 
    useQuery 
} from "@apollo/client";
import { 
    FindAllCustomerTiersQueryQuery, 
    FindAllCustomerTiersQueryQueryVariables
} from "../../../../../../schemaTypes";
import { useCallback } from "react";


export const findAllCustomerTiersQuery = gql`
  query FindAllCustomerTiersQuery(
    $page: Int!
    $pageSize: Int!
    $searchText: String
    $sortBy: String
    $sortOrder: String
  ) {
    findAllCustomerTiers(
      page: $page
      pageSize: $pageSize
      searchText: $searchText
      sortBy: $sortBy
      sortOrder: $sortOrder
    ) {
      data {
        id
        name
        multiplier
        threshold
        createdAt
        customerCount
        isArchive
      }
      total
      page
      pageSize
      totalPages
    }
  }
`;

export interface WithFindAllCustomerTiers {
  data: FindAllCustomerTiersQueryQuery["findAllCustomerTiers"]["data"]
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  loading: boolean;
  loadMore: () => void;
  hasMoreTiers: boolean;
}

interface Props {
  variables: FindAllCustomerTiersQueryQueryVariables;
  children: (data: WithFindAllCustomerTiers) => React.ReactElement | null;
}

export const FindAllCustomerTiersController: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    FindAllCustomerTiersQueryQuery,
    FindAllCustomerTiersQueryQueryVariables
  >(findAllCustomerTiersQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const findAllCustomerTiersData = data?.findAllCustomerTiers?.data || [];
  const page = data?.findAllCustomerTiers?.page || variables.page;
  const pageSize = data?.findAllCustomerTiers?.pageSize || variables.pageSize;
  const total = data?.findAllCustomerTiers?.total || 0;
  const totalPages = data?.findAllCustomerTiers?.totalPages || 0;
  const hasMoreTiers = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTiers) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult?.findAllCustomerTiers) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findAllCustomerTiers: {
            ...fetchMoreResult?.findAllCustomerTiers,
            data: [
              ...(prev?.findAllCustomerTiers?.data || []),
              ...(fetchMoreResult?.findAllCustomerTiers?.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTiers]);

  return children({
    data: findAllCustomerTiersData as any,
    loading,
    loadMore,
    hasMoreTiers,
    page,
    pageSize,
    total,
    totalPages,
  });
};